import React, { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Chip,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  Preview as PreviewIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import axios from 'axios';

dayjs.extend(utc);
dayjs.extend(timezone);

const SYDNEY_TZ = 'Australia/Sydney';

/** MUI DateTimePicker with `timezone` stores the correct instant; serialize as UTC ISO for the API. */
function toUtcIsoFromPicker(value) {
  if (!value || typeof value.isValid !== 'function' || !value.isValid()) return null;
  return value.toISOString();
}

const ManifestSuccessfulSends = () => {
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewEmailOpen, setViewEmailOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [resending, setResending] = useState(false);
  const [sendToDialogOpen, setSendToDialogOpen] = useState(false);
  const [selectedSendForCustom, setSelectedSendForCustom] = useState(null);
  const [customEmail, setCustomEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [quickStart, setQuickStart] = useState(null);
  const [quickEnd, setQuickEnd] = useState(null);
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickError, setQuickError] = useState('');

  useEffect(() => {
    loadSuccessfulSends();
  }, []);

  const loadSuccessfulSends = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/manifest/successful-sends', {
        params: { limit: 100 },
      });
      setSends(response.data);
    } catch (err) {
      setError('Failed to load successful sends');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewEmail = async (send) => {
    if (!send.slot_start_time) {
      setError('Slot start time not available for this email');
      return;
    }

    try {
      setResending(true);
      // Use resend endpoint with preview_only=true to get email body without sending
      const response = await axios.post('/manifest/resend-email', {
        slot_start_time: send.slot_start_time,
        recipients: send.recipients,
        preview_only: true,
      });

      if (response.data.email_body) {
        setSelectedEmail({
          ...send,
          email_body: response.data.email_body,
          subject: response.data.subject,
        });
        setViewEmailOpen(true);
      } else {
        setError('Email content not available');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load email content');
    } finally {
      setResending(false);
    }
  };

  const handleResendEmail = async (send) => {
    if (!send.slot_start_time) {
      setError('Slot start time not available for this email');
      return;
    }

    if (!window.confirm(`Resend email to ${send.recipients.join(', ')}?`)) {
      return;
    }

    try {
      setResending(true);
      const response = await axios.post('/manifest/resend-email', {
        slot_start_time: send.slot_start_time,
        recipients: send.recipients,
      });

      if (response.data.email_body) {
        setSelectedEmail({
          ...send,
          email_body: response.data.email_body,
          subject: response.data.subject,
        });
        setViewEmailOpen(true);
      }

      setSuccess(`Email resent successfully to ${send.recipients.join(', ')}`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend email');
      setTimeout(() => setError(''), 5000);
    } finally {
      setResending(false);
    }
  };

  const handleCloseEmailModal = () => {
    setViewEmailOpen(false);
    setSelectedEmail(null);
  };

  const handleSendToClick = (send) => {
    if (!send.slot_start_time) {
      setError('Slot start time not available for this email');
      return;
    }
    setSelectedSendForCustom(send);
    setCustomEmail('');
    setEmailError('');
    setSendToDialogOpen(true);
  };

  const handleSendToClose = () => {
    setSendToDialogOpen(false);
    setSelectedSendForCustom(null);
    setCustomEmail('');
    setEmailError('');
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendToSubmit = async () => {
    if (!customEmail.trim()) {
      setEmailError('Email address is required');
      return;
    }

    if (!validateEmail(customEmail.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (!selectedSendForCustom) {
      return;
    }

    try {
      setResending(true);
      setEmailError('');
      await axios.post('/manifest/resend-email', {
        slot_start_time: selectedSendForCustom.slot_start_time,
        recipients: selectedSendForCustom.recipients,
        custom_recipient: customEmail.trim(),
      });

      setSuccess(`Email sent successfully to ${customEmail.trim()}`);
      setTimeout(() => setSuccess(''), 5000);
      handleSendToClose();
      loadSuccessfulSends();
    } catch (err) {
      setEmailError(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setResending(false);
    }
  };

  const handleQuickManifest = async () => {
    const slotStartUtc = toUtcIsoFromPicker(quickStart);
    if (!slotStartUtc) {
      setQuickError('Choose the tour start date and time (Sydney).');
      return;
    }
    const slotEndUtc = toUtcIsoFromPicker(quickEnd);
    if (quickEnd && !slotEndUtc) {
      setQuickError('End time is not valid. Clear it or choose a full date and time (Sydney).');
      return;
    }
    setQuickError('');
    setQuickLoading(true);
    setError('');
    try {
      const payload = { slot_start_time: slotStartUtc };
      if (slotEndUtc) payload.slot_end_time = slotEndUtc;
      const response = await axios.post('/manifest/preview-by-slot', payload);
      if (response.data.email_body) {
        setSelectedEmail({
          timestamp: '',
          recipients: [],
          subject: response.data.subject,
          email_body: response.data.email_body,
          slot_start_time: slotStartUtc,
        });
        setViewEmailOpen(true);
      } else {
        setQuickError('No manifest HTML returned');
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setQuickError(typeof d === 'string' ? d : err.message || 'Failed to generate manifest');
    } finally {
      setQuickLoading(false);
    }
  };

  const handleDownloadManifestHtml = () => {
    if (!selectedEmail?.email_body) return;
    const safeName = (selectedEmail.subject || 'manifest')
      .replace(/[^a-zA-Z0-9-_]+/g, '_')
      .slice(0, 80);
    const blob = new Blob([selectedEmail.email_body], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyManifestHtml = async () => {
    if (!selectedEmail?.email_body) return;
    try {
      await navigator.clipboard.writeText(selectedEmail.email_body);
      setSuccess('HTML copied to clipboard');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  return (
    <Box>
      <Accordion
        defaultExpanded={false}
        disableGutters
        variant="outlined"
        sx={{ mb: 3, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={700}>
            Quick manifest (preview only, no email)
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Build a participant list you can view, copy, or save as HTML—nothing is emailed. Use the{' '}
              <strong>same tour start</strong> you see in Bookeo, in <strong>Sydney time</strong>. Bookings are loaded
              from that time for about <strong>three hours</strong> by default. If your tour is longer or bookings sit
              outside that window, set an optional <strong>end time</strong> (also Sydney time).
            </Typography>
            {quickError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setQuickError('')}>
                {quickError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <DateTimePicker
                label="Tour start (Sydney)"
                timezone={SYDNEY_TZ}
                value={quickStart}
                onChange={setQuickStart}
                disabled={quickLoading}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 280, flex: '1 1 260px' },
                  },
                }}
              />
              <DateTimePicker
                label="Tour end (optional, Sydney)"
                timezone={SYDNEY_TZ}
                value={quickEnd}
                onChange={setQuickEnd}
                disabled={quickLoading}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 280, flex: '1 1 260px' },
                    helperText: 'Leave empty to use start plus 3 hours',
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<PreviewIcon />}
                onClick={handleQuickManifest}
                disabled={quickLoading}
                sx={{ mt: 0.5 }}
              >
                {quickLoading ? 'Loading…' : 'Generate'}
              </Button>
            </Box>
          </LocalizationProvider>
        </AccordionDetails>
      </Accordion>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Tour Name</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Recipients</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sends.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      No successful sends found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sends.map((send, index) => (
                  <TableRow key={index}>
                    <TableCell>{send.timestamp}</TableCell>
                    <TableCell>{send.tour_name || 'N/A'}</TableCell>
                    <TableCell>
                      {send.tour_date && send.tour_time
                        ? `${send.tour_date} at ${send.tour_time}`
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {send.recipients.map((email, idx) => (
                          <Chip key={idx} label={email} size="small" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewEmail(send)}
                          disabled={resending || !send.slot_start_time}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<SendIcon />}
                          onClick={() => handleResendEmail(send)}
                          disabled={resending || !send.slot_start_time}
                        >
                          Resend
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          startIcon={<EmailIcon />}
                          onClick={() => handleSendToClick(send)}
                          disabled={resending || !send.slot_start_time}
                        >
                          Send To...
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={viewEmailOpen}
        onClose={handleCloseEmailModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedEmail?.subject || 'Email Preview'}
            </Typography>
            <IconButton onClick={handleCloseEmailModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEmail?.email_body ? (
            <Box
              dangerouslySetInnerHTML={{ __html: selectedEmail.email_body }}
              sx={{
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                },
                '& th, & td': {
                  border: '1px solid #ddd',
                  padding: '8px',
                  textAlign: 'left',
                },
                '& th': {
                  backgroundColor: '#f4f4f4',
                },
              }}
            />
          ) : (
            <Typography color="text.secondary">
              Email content not available. Click "Resend" to view the email.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button startIcon={<DownloadIcon />} onClick={handleDownloadManifestHtml} disabled={!selectedEmail?.email_body}>
            Download HTML
          </Button>
          <Button startIcon={<ContentCopyIcon />} onClick={handleCopyManifestHtml} disabled={!selectedEmail?.email_body}>
            Copy HTML
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleCloseEmailModal}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sendToDialogOpen} onClose={handleSendToClose} maxWidth="sm" fullWidth>
        <DialogTitle>Send Manifest to Alternate Email</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {selectedSendForCustom && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tour: {selectedSendForCustom.tour_name || 'N/A'}<br />
                Date: {selectedSendForCustom.tour_date || 'N/A'} at {selectedSendForCustom.tour_time || 'N/A'}
              </Typography>
            )}
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={customEmail}
              onChange={(e) => {
                setCustomEmail(e.target.value);
                setEmailError('');
              }}
              placeholder="recipient@example.com"
              error={!!emailError}
              helperText={emailError || 'Enter the email address to send this manifest to'}
              disabled={resending}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSendToClose} disabled={resending}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendToSubmit} 
            variant="contained" 
            color="primary"
            disabled={resending || !customEmail.trim()}
          >
            {resending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManifestSuccessfulSends;

